import type { JSX } from 'react';

/**
 * ISA-5.1 symbol library. Every symbol is a pure function of engine state — there is no
 * scripted animation anywhere (CLAUDE.md rule 6) — and every state that matters is
 * signalled by shape or motion as well as colour (rule 10).
 */

export interface SymbolCtx {
  /** Read a live tag. Returns 0 if the tag does not exist. */
  v(name: string): number;
  /** True when any alarm on this equipment is unacknowledged. */
  alarm: 'none' | 'acked' | 'unacked';
  label?: string;
  simTime: number;
}

const LINE = 'var(--line)';
const DIM = 'var(--line-dim)';
const FILL = 'var(--panel)';

function Tag({
  x,
  y,
  text,
  anchor = 'middle',
}: {
  x: number;
  y: number;
  text: string;
  anchor?: 'start' | 'middle' | 'end';
}): JSX.Element {
  return (
    <text x={x} y={y} textAnchor={anchor} fontSize={10} fill="var(--text-dim)" fontFamily="var(--mono)">
      {text}
    </text>
  );
}

/** Fraction 0–1 → a rising fill inside a rectangle. */
function LevelFill({
  x,
  y,
  w,
  h,
  frac,
  colour = 'var(--fluid)',
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  frac: number;
  colour?: string;
}): JSX.Element {
  const f = Math.max(0, Math.min(1, frac));
  return <rect x={x} y={y + h * (1 - f)} width={w} height={h * f} fill={colour} opacity={0.55} />;
}

export type SymbolRenderer = (id: string, ctx: SymbolCtx) => JSX.Element;

const tank: SymbolRenderer = (id, c) => {
  const frac = c.v(`${id}.LEVELPCT`) / 100;
  const overflow = frac > 0.985;
  return (
    <g>
      <rect x={-32} y={-38} width={64} height={76} rx={3} fill={FILL} stroke={LINE} strokeWidth={1.6} />
      <LevelFill x={-31} y={-37} w={62} h={74} frac={frac} />
      <line x1={-32} y1={-38 + 76 * 0.1} x2={-26} y2={-38 + 76 * 0.1} stroke={DIM} />
      <line x1={-32} y1={-38 + 76 * 0.5} x2={-26} y2={-38 + 76 * 0.5} stroke={DIM} />
      <line x1={-32} y1={-38 + 76 * 0.9} x2={-26} y2={-38 + 76 * 0.9} stroke={DIM} />
      <rect x={-32} y={-38} width={64} height={76} rx={3} fill="none" stroke={LINE} strokeWidth={1.6} />
      {overflow && (
        <path d="M -12 -46 L 0 -56 L 12 -46 Z" fill="var(--pri-high)" stroke="none" />
      )}
      <Tag x={0} y={52} text={c.label ?? id} />
      <text x={0} y={4} textAnchor="middle" fontSize={12} fill="var(--text)" fontFamily="var(--mono)">
        {(frac * 100).toFixed(0)}%
      </text>
    </g>
  );
};

const valve: SymbolRenderer = (id, c) => {
  const pos = c.v(`${id}.POS`) / 100;
  const closed = pos < 0.02;
  const open = pos > 0.98;
  // The wedge width carries the position; the letter carries it too.
  const w = 18;
  return (
    <g>
      <path d={`M ${-w} -11 L ${-w} 11 L 0 0 Z`} fill={closed ? FILL : 'var(--fluid)'} stroke={LINE} strokeWidth={1.4} />
      <path d={`M ${w} -11 L ${w} 11 L 0 0 Z`} fill={closed ? FILL : 'var(--fluid)'} stroke={LINE} strokeWidth={1.4} />
      <line x1={0} y1={0} x2={0} y2={-17} stroke={LINE} strokeWidth={1.4} />
      <rect x={-9} y={-24} width={18} height={7} fill={FILL} stroke={LINE} strokeWidth={1.4} />
      {/* Position bar — shape, not just colour. */}
      <rect x={-14} y={16} width={28} height={5} fill="none" stroke={DIM} />
      <rect x={-14} y={16} width={28 * Math.max(0, Math.min(1, pos))} height={5} fill="var(--fluid)" />
      <Tag x={0} y={34} text={c.label ?? id} />
      <text x={0} y={-28} textAnchor="middle" fontSize={9} fill="var(--text-mute)" fontFamily="var(--mono)">
        {closed ? 'SHUT' : open ? 'OPEN' : `${(pos * 100).toFixed(0)}%`}
      </text>
    </g>
  );
};

const pump: SymbolRenderer = (id, c) => {
  const status = Math.round(c.v(`${id}.STATUS`));
  const speed = c.v(`${id}.SPEED`) / 100;
  const running = status === 1 || status === 3 || status === 4;
  const colour =
    status === 2 ? 'var(--pri-high)' : status === 3 || status === 4 ? 'var(--pri-medium)' : running ? 'var(--fluid)' : FILL;
  // The rotor mark rotates at the pump's actual speed — motion carrying information.
  const angle = running ? (c.simTime * speed * 220) % 360 : 0;
  return (
    <g>
      <circle r={17} fill={colour} stroke={LINE} strokeWidth={1.6} opacity={running ? 0.85 : 1} />
      <path d="M -17 0 L 8 -14 L 8 14 Z" fill="none" stroke={LINE} strokeWidth={1.4} />
      <g transform={`rotate(${angle})`}>
        <line x1={0} y1={0} x2={0} y2={-11} stroke="var(--text)" strokeWidth={2} />
      </g>
      {status === 2 && (
        <text x={0} y={31} textAnchor="middle" fontSize={9} fontWeight={700} fill="var(--pri-high)">
          TRIP
        </text>
      )}
      {status === 3 && (
        <text x={0} y={31} textAnchor="middle" fontSize={9} fontWeight={700} fill="var(--pri-medium)">
          CAV
        </text>
      )}
      {status === 4 && (
        <text x={0} y={31} textAnchor="middle" fontSize={9} fontWeight={700} fill="var(--pri-medium)">
          MIN FLOW
        </text>
      )}
      <Tag x={0} y={status >= 2 ? 43 : 31} text={c.label ?? id} />
    </g>
  );
};

const source: SymbolRenderer = (id, c) => (
  <g>
    <path d="M -26 -14 L 14 -14 L 26 0 L 14 14 L -26 14 Z" fill={FILL} stroke={LINE} strokeWidth={1.5} />
    <path d="M -20 6 q 6 -8 12 0 t 12 0" fill="none" stroke="var(--fluid)" strokeWidth={1.6} />
    <Tag x={0} y={30} text={c.label ?? id} />
  </g>
);

const sink: SymbolRenderer = (id, c) => (
  <g>
    <path d="M -26 -14 L 14 -14 L 26 0 L 14 14 L -26 14 Z" fill={FILL} stroke={LINE} strokeWidth={1.5} opacity={0.75} />
    <Tag x={0} y={30} text={c.label ?? id} />
  </g>
);

const header: SymbolRenderer = (id, c) => (
  <g>
    <circle r={7} fill={FILL} stroke={LINE} strokeWidth={1.5} />
    <text x={0} y={-13} textAnchor="middle" fontSize={9.5} fill="var(--text-dim)" fontFamily="var(--mono)">
      {c.v(`${id}.P`).toFixed(2)} barg
    </text>
    <Tag x={0} y={22} text={c.label ?? id} />
  </g>
);

const doser: SymbolRenderer = (id, c) => {
  const lph = c.v(`${id}.LPH`);
  const on = lph > 0.1;
  return (
    <g>
      <rect x={-20} y={-16} width={40} height={26} rx={2} fill={FILL} stroke={LINE} strokeWidth={1.5} />
      <circle cx={0} cy={-3} r={7} fill={on ? 'var(--utility)' : FILL} stroke={LINE} strokeWidth={1.3} />
      <line x1={-20} y1={-16} x2={20} y2={-16} stroke={LINE} strokeWidth={1.5} />
      <text x={0} y={22} textAnchor="middle" fontSize={9.5} fill="var(--text-dim)" fontFamily="var(--mono)">
        {lph.toFixed(1)} L/h
      </text>
      <Tag x={0} y={34} text={c.label ?? id} />
    </g>
  );
};

const coagulation: SymbolRenderer = (id, c) => {
  const dose = c.v(`${id}.DOSE`);
  const opt = c.v(`${id}.DOSE_OPT`);
  const off = opt > 0 ? Math.abs(dose - opt) / opt : 0;
  const bad = off > 0.35;
  return (
    <g>
      <rect x={-26} y={-28} width={52} height={56} rx={2} fill={FILL} stroke={LINE} strokeWidth={1.6} />
      <line x1={0} y1={-28} x2={0} y2={4} stroke={LINE} strokeWidth={1.6} />
      <g transform={`rotate(${(c.simTime * 300) % 360})`}>
        <line x1={-11} y1={4} x2={11} y2={4} stroke={LINE} strokeWidth={2.4} />
      </g>
      <rect x={-25} y={-2} width={50} height={29} fill="var(--fluid)" opacity={0.4} />
      {bad && <circle cx={17} cy={-20} r={5} fill="var(--pri-medium)" />}
      <Tag x={0} y={42} text={c.label ?? id} />
    </g>
  );
};

const floc: SymbolRenderer = (id, c) => (
  <g>
    <rect x={-38} y={-28} width={76} height={56} rx={2} fill={FILL} stroke={LINE} strokeWidth={1.6} />
    <rect x={-37} y={-6} width={74} height={33} fill="var(--fluid)" opacity={0.4} />
    {[-24, 0, 24].map((dx) => (
      <g key={dx} transform={`translate(${dx} 2) rotate(${(c.simTime * 40 + dx) % 360})`}>
        <line x1={-9} y1={0} x2={9} y2={0} stroke={LINE} strokeWidth={1.8} />
        <line x1={0} y1={-9} x2={0} y2={9} stroke={DIM} strokeWidth={1.2} />
      </g>
    ))}
    <line x1={-13} y1={-28} x2={-13} y2={28} stroke={DIM} strokeDasharray="3 3" />
    <line x1={13} y1={-28} x2={13} y2={28} stroke={DIM} strokeDasharray="3 3" />
    <Tag x={0} y={42} text={c.label ?? id} />
  </g>
);

const clarifier: SymbolRenderer = (id, c) => {
  const blanket = c.v(`${id}.BLANKET`);
  const frac = Math.max(0, Math.min(1, blanket / 3));
  return (
    <g>
      <path d="M -40 -30 L 40 -30 L 40 12 L 0 32 L -40 12 Z" fill={FILL} stroke={LINE} strokeWidth={1.6} />
      <path d="M -39 -8 L 39 -8 L 39 11 L 0 31 L -39 11 Z" fill="var(--fluid)" opacity={0.35} />
      {/* Sludge blanket — its height is the state, drawn as a solid band. */}
      <path
        d={`M -39 ${31 - 2 - 38 * frac} L 39 ${31 - 2 - 38 * frac} L 39 11 L 0 31 L -39 11 Z`}
        fill={frac > 0.62 ? 'var(--pri-medium)' : '#8a7a5e'}
        opacity={0.85}
      />
      <line x1={-40} y1={-30} x2={40} y2={-30} stroke={LINE} strokeWidth={1.6} />
      <Tag x={0} y={46} text={c.label ?? id} />
    </g>
  );
};

const filter: SymbolRenderer = (id, c) => {
  const state = Math.round(c.v(`${id}.STATE`));
  const hl = c.v(`${id}.HL`);
  const level = c.v(`${id}.LEVEL`);
  const stateName = ['SERVICE', 'BACKWASH', 'STANDBY', 'TO WASTE'][state] ?? '';
  const stateColour =
    state === 0 ? 'var(--text-dim)' : state === 1 ? 'var(--info)' : 'var(--pri-medium)';
  const hlFrac = Math.max(0, Math.min(1, hl / 2.5));
  return (
    <g>
      <rect x={-30} y={-24} width={60} height={48} rx={2} fill={FILL} stroke={LINE} strokeWidth={1.6} />
      <rect x={-29} y={-23} width={58} height={22 * Math.max(0, Math.min(1, level / 2))} fill="var(--fluid)" opacity={0.45} />
      {/* Media bed */}
      <rect x={-29} y={2} width={58} height={13} fill="#6f6a60" opacity={0.85} />
      <rect x={-29} y={15} width={58} height={8} fill="#575249" opacity={0.9} />
      {/* Headloss bar: shape carries the value, colour only marks the alarm. */}
      <rect x={-29} y={-30} width={58} height={4} fill="none" stroke={DIM} />
      <rect
        x={-29}
        y={-30}
        width={58 * hlFrac}
        height={4}
        fill={hl > 2.2 ? 'var(--pri-medium)' : 'var(--line)'}
      />
      <text x={0} y={38} textAnchor="middle" fontSize={9} fill={stateColour} fontFamily="var(--mono)">
        {stateName}
      </text>
      <Tag x={0} y={50} text={c.label ?? id} />
    </g>
  );
};

const contact: SymbolRenderer = (id, c) => {
  const ratio = c.v(`${id}.CTRATIO`);
  return (
    <g>
      <rect x={-34} y={-24} width={68} height={48} rx={2} fill={FILL} stroke={LINE} strokeWidth={1.6} />
      <rect x={-33} y={-8} width={66} height={31} fill="var(--fluid)" opacity={0.4} />
      {[-18, -4, 10, 24].map((x, i) => (
        <line key={x} x1={x} y1={i % 2 ? -24 : 24} x2={x} y2={i % 2 ? 12 : -12} stroke={DIM} strokeWidth={1.4} />
      ))}
      <text x={0} y={38} textAnchor="middle" fontSize={9.5} fontFamily="var(--mono)" fill={ratio < 1 ? 'var(--pri-emergency)' : 'var(--text-dim)'}>
        CT {ratio.toFixed(2)}
      </text>
      <Tag x={0} y={50} text={c.label ?? id} />
    </g>
  );
};

const cstr: SymbolRenderer = (id, c) => {
  const frac = c.v(`${id}.LEVELPCT`) / 100;
  const T = c.v(`${id}.T`);
  const hot = T > 375;
  return (
    <g>
      <rect x={-30} y={-40} width={60} height={80} rx={16} fill={FILL} stroke={LINE} strokeWidth={1.7} />
      <rect x={-36} y={-30} width={72} height={62} rx={6} fill="none" stroke={DIM} strokeWidth={1.4} strokeDasharray="4 3" />
      <LevelFill x={-29} y={-39} w={58} h={78} frac={frac} colour={hot ? 'var(--pri-high)' : 'var(--fluid)'} />
      <line x1={0} y1={-46} x2={0} y2={10} stroke={LINE} strokeWidth={1.7} />
      <g transform={`translate(0 10) rotate(${(c.simTime * 400) % 360})`}>
        <line x1={-13} y1={0} x2={13} y2={0} stroke={LINE} strokeWidth={2.6} />
      </g>
      <rect x={-30} y={-40} width={60} height={80} rx={16} fill="none" stroke={LINE} strokeWidth={1.7} />
      <text x={0} y={-2} textAnchor="middle" fontSize={11} fontFamily="var(--mono)" fill="var(--text)">
        {(T - 273.15).toFixed(1)}°C
      </text>
      <Tag x={0} y={56} text={c.label ?? id} />
    </g>
  );
};

const separator: SymbolRenderer = (id, c) => {
  const frac = c.v(`${id}.LEVEL`) / 100;
  const relief = c.v(`${id}.RELIEF`) > 0.5;
  return (
    <g>
      <rect x={-46} y={-22} width={92} height={44} rx={22} fill={FILL} stroke={LINE} strokeWidth={1.7} />
      <rect x={-45} y={22 - 43 * Math.max(0, Math.min(1, frac))} width={90} height={43 * Math.max(0, Math.min(1, frac))} fill="var(--fluid)" opacity={0.5} />
      <rect x={-46} y={-22} width={92} height={44} rx={22} fill="none" stroke={LINE} strokeWidth={1.7} />
      <text x={0} y={-2} textAnchor="middle" fontSize={10} fontFamily="var(--mono)" fill="var(--text)">
        {c.v(`${id}.P`).toFixed(2)} bara
      </text>
      {relief && (
        <g>
          <path d="M 0 -22 L 0 -38" stroke="var(--pri-emergency)" strokeWidth={2.5} />
          <path d="M -8 -38 L 8 -38 L 0 -50 Z" fill="var(--pri-emergency)" />
        </g>
      )}
      <Tag x={0} y={38} text={c.label ?? id} />
    </g>
  );
};

const hx: SymbolRenderer = (id, c) => (
  <g>
    <rect x={-38} y={-20} width={76} height={40} rx={3} fill={FILL} stroke={LINE} strokeWidth={1.6} />
    <path d="M -30 -12 L 30 -12 M -30 0 L 30 0 M -30 12 L 30 12" stroke={DIM} strokeWidth={1.3} />
    <text x={0} y={34} textAnchor="middle" fontSize={9.5} fontFamily="var(--mono)" fill="var(--text-dim)">
      {c.v(`${id}.DUTY`).toFixed(0)} kW
    </text>
    <Tag x={0} y={46} text={c.label ?? id} />
  </g>
);

const heater: SymbolRenderer = (id, c) => {
  const skin = c.v(`${id}.TSKIN`);
  const flame = c.v(`${id}.FLAME`) > 0.5;
  return (
    <g>
      <rect x={-34} y={-38} width={68} height={76} rx={3} fill={FILL} stroke={LINE} strokeWidth={1.7} />
      <path d="M -22 30 q 6 -16 0 -26 q 12 8 10 26 Z" fill={flame ? 'var(--pri-high)' : DIM} opacity={flame ? 0.9 : 0.4} />
      <path d="M 12 30 q 6 -16 0 -26 q 12 8 10 26 Z" fill={flame ? 'var(--pri-high)' : DIM} opacity={flame ? 0.9 : 0.4} />
      <path d="M -26 -22 L 26 -22 M -26 -8 L 26 -8" stroke={DIM} strokeWidth={1.6} />
      <text x={0} y={-28} textAnchor="middle" fontSize={9.5} fontFamily="var(--mono)" fill={skin > 723 ? 'var(--pri-high)' : 'var(--text-dim)'}>
        skin {(skin - 273.15).toFixed(0)}°C
      </text>
      {!flame && (
        <text x={0} y={4} textAnchor="middle" fontSize={10} fontWeight={700} fill="var(--pri-emergency)">
          NO FLAME
        </text>
      )}
      <Tag x={0} y={52} text={c.label ?? id} />
    </g>
  );
};

const column: SymbolRenderer = (id, c) => {
  const flooding = c.v(`${id}.FLOOD`) > 0.5;
  const dp = c.v(`${id}.DP`);
  return (
    <g>
      <rect x={-26} y={-110} width={52} height={220} rx={26} fill={FILL} stroke={LINE} strokeWidth={1.8} />
      {Array.from({ length: 9 }, (_, i) => -92 + i * 24).map((y) => (
        <line key={y} x1={-25} y1={y} x2={25} y2={y} stroke={DIM} strokeWidth={1.2} />
      ))}
      <rect x={-25} y={70} width={50} height={39} fill="var(--fluid)" opacity={0.45} />
      <text x={0} y={-120} textAnchor="middle" fontSize={9.5} fontFamily="var(--mono)" fill={flooding ? 'var(--pri-high)' : 'var(--text-dim)'}>
        ΔP {dp.toFixed(3)} bar{flooding ? ' — FLOODING' : ''}
      </text>
      <Tag x={0} y={126} text={c.label ?? id} />
    </g>
  );
};

const demand: SymbolRenderer = (id, c) => (
  <g>
    <path d="M -18 -10 L 10 -10 L 10 -16 L 24 0 L 10 16 L 10 10 L -18 10 Z" fill={FILL} stroke={LINE} strokeWidth={1.4} />
    <Tag x={0} y={28} text={c.label ?? id} />
  </g>
);

export const SYMBOLS: Record<string, SymbolRenderer> = {
  tank,
  valve,
  pump,
  source,
  sink,
  header,
  doser,
  coagulation,
  floc,
  clarifier,
  filter,
  contact,
  cstr,
  separator,
  hx,
  heater,
  column,
  demand,
};
